import * as React from "react";
import { useState, useEffect, useMemo } from "react";
import { cn } from "../../lib/utils";
import { useAuth } from "../../auth/AuthProvider";
import { useToast } from "../ui/toast";
import type {
  ConsultingRequestInput,
  ConsultingExpertise,
} from "../../lib/datasource/types";

import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { createConsultingRequest } from "@/lib/datasource/supabase";

type Seniority = "junior" | "standard" | "partner";

const HOURLY: Record<Seniority, number> = {
  junior: 80,
  standard: 150,
  partner: 300,
};

const expertiseOptions: [ConsultingExpertise, string, string][] = [
  ["operations", "Operations", "Staffing, standards, guest experience"],
  ["finance", "Finance", "P&L review, pricing, optimization"],
  ["development", "Development", "Project mgmt, permits, construction"],
  ["other", "Other", "Marketing, branding, tech, legal"],
];

const seniorityOptions: [Seniority, string, string][] = [
  ["junior", "Junior", `€${HOURLY.junior}/hr · great for scoped tasks`],
  ["standard", "Standard", `€${HOURLY.standard}/hr · best for most requests`],
  ["partner", "Partner", `€${HOURLY.partner}/hr · industry leader review`],
];

export default function ConsultingRequestForm() {
  const { user } = useAuth();
  const { toast } = useToast();
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [expertise, setExpertise] = useState<ConsultingExpertise>("operations");
  const [hoursHint, setHoursHint] = useState<string>("");
  const [summary, setSummary] = useState("");
  const [seniority, setSeniority] = useState<Seniority>("standard");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (user) {
      setName((v) => v || user.name || "");
      setEmail((v) => v || user.email || "");
    }
  }, [user?.id]);

  const currentRate = HOURLY[seniority] || 0;

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitting(true);

    try {
      if (!email.trim() || !summary.trim()) {
        toast.error("Please provide your email and a short project summary.");
        setSubmitting(false);
        return;
      }

      const payload: ConsultingRequestInput = {
        name,
        email,
        expertise,
        seniority,
        estimatedHours: hoursHint ? Number(hoursHint) || null : null,
        message: summary,
      };

      const { data, error } = await createConsultingRequest(payload);

      if (error) {
        toast.error(error);
        return;
      }

      toast.success("Request sent. We'll reply within 24h.");
      setSummary("");
      setHoursHint("");
      setExpertise("operations");
      if (!user) {
        setName("");
        setEmail("");
      }
    } catch (err: any) {
      console.error("[Consulting submit] failed:", err);
      toast.error(err?.message || "Could not submit request");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="mx-auto max-w-4xl">
      <Card className="rounded-xl shadow-lg">
        <CardHeader className="p-6">
          <CardTitle className="text-2xl font-semibold">
            Hotel Consulting Request
          </CardTitle>
          <p className="mt-1 text-sm text-muted-foreground">
            Tell us where you need help. We'll match the right specialist and
            send a fixed quote within 24 hours.
          </p>

          <div className="mt-4 rounded-xl border bg-slate-50 p-4">
            <ul className="grid gap-2 text-sm">
              <li>
                • Most questions are solved with a short review (1–2 hours).
              </li>
              <li>• Larger projects can be quoted at volume rates.</li>
              <li>
                • Current rate estimate:{" "}
                <strong className="text-primary-blue">
                  €{currentRate} per hour
                </strong>{" "}
                (based on selected seniority).
              </li>
            </ul>
          </div>
        </CardHeader>

        <CardContent className="p-6 pt-0">
          <form onSubmit={onSubmit} className="space-y-8">
            <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
              <div className="space-y-2">
                <Label htmlFor="name">Name</Label>
                <Input
                  id="name"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="Jane Doe"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="email">
                  Email <span className="text-rose-600">*</span>
                </Label>
                <Input
                  required
                  id="email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@example.com"
                />
                {user && email === user.email && (
                  <p className="mt-1 text-xs text-slate-500">
                    Using your account email
                  </p>
                )}
              </div>
            </div>

            <div className="space-y-3">
              <Label>
                Expertise Needed <span className="text-rose-600">*</span>
              </Label>
              <RadioGroup
                value={expertise}
                onValueChange={(v) => setExpertise(v as ConsultingExpertise)}
                className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-4"
              >
                {expertiseOptions.map(([key, title, desc]) => (
                  <div
                    key={key}
                    className={cn(
                      "relative rounded-lg border p-4 cursor-pointer hover:bg-slate-50 transition-colors",
                      expertise === key &&
                        "border-[hsl(215_85%_45%)] ring-2 ring-[hsl(215_85%_45%)] bg-[hsl(215_85%_45%/0.1)]", // 10% opacity
                    )}
                  >
                    <RadioGroupItem
                      value={key}
                      id={`expertise-${key}`}
                      className="sr-only"
                    />
                    <Label
                      htmlFor={`expertise-${key}`}
                      className="flex flex-col space-y-1 cursor-pointer"
                    >
                      <span className="text-sm font-semibold">{title}</span>
                      <span className="text-xs text-muted-foreground">
                        {desc}
                      </span>
                    </Label>
                    {expertise === key && (
                      <div className="absolute top-2 right-2 h-2 w-2 rounded-full bg-[hsl(215_85%_45%)]" />
                    )}
                  </div>
                ))}
              </RadioGroup>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-[1fr_400px] gap-8">
              <div className="space-y-2">
                <Label htmlFor="summary">
                  What do you need help with?{" "}
                  <span className="text-rose-600">*</span>
                </Label>
                <Textarea
                  required
                  id="summary"
                  rows={10}
                  placeholder="Describe your property, timeline, budget, specific questions, and desired outcome..."
                  value={summary}
                  onChange={(e) => setSummary(e.target.value)}
                />
                <p className="mt-1 text-xs text-muted-foreground">
                  The clearer the brief, the faster we can help. Maximize
                  clarity in this summary.
                </p>
              </div>

              <div className="space-y-6">
                <div className="space-y-3">
                  <Label>Preferred Seniority & Rate</Label>
                  <RadioGroup
                    value={seniority}
                    onValueChange={(v) => setSeniority(v as Seniority)}
                    className="grid grid-cols-1 gap-3"
                  >
                    {seniorityOptions.map(([key, label, note]) => (
                      <div
                        key={key}
                        className={cn(
                          "relative rounded-lg border p-3 cursor-pointer hover:bg-slate-50 transition-colors",
                          seniority === key
                            ? "border-[hsl(215_85%_45%)] ring-2 ring-[hsl(215_85%_45%)] bg-[hsl(215_85%_45%/0.1)]"
                            : "",
                        )}
                      >
                        <RadioGroupItem
                          value={key}
                          id={`seniority-${key}`}
                          className="sr-only"
                        />
                        <Label
                          htmlFor={`seniority-${key}`}
                          className="flex flex-col space-y-1 cursor-pointer"
                        >
                          <span className="text-sm font-semibold">{label}</span>
                          <span className="text-xs text-muted-foreground">
                            {note}
                          </span>
                        </Label>
                        {seniority === key && (
                          <div className="absolute top-2 right-2 h-2 w-2 rounded-full bg-[hsl(215_85%_45%)]" />
                        )}
                      </div>
                    ))}
                  </RadioGroup>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="hours">Estimated Hours (optional)</Label>
                  <Input
                    id="hours"
                    inputMode="numeric"
                    pattern="[0-9]*"
                    placeholder="e.g., 2"
                    value={hoursHint}
                    onChange={(e) =>
                      setHoursHint(e.target.value.replace(/[^0-9]/g, ""))
                    }
                  />
                  <p className="mt-1 text-xs text-muted-foreground">
                    We'll confirm scope and provide a fixed quote in our
                    proposal.
                  </p>
                </div>
              </div>
            </div>

            <div className="flex items-center justify-between pt-4 border-t">
              <p className="text-sm text-muted-foreground">
                <span className="font-medium">Response time:</span> within 24
                hours.
              </p>
              <Button
                type="submit"
                disabled={submitting || !email.trim() || !summary.trim()}
                className="px-6 py-2.5 bg-primary-blue"
              >
                {submitting ? "Submitting..." : "Request a Proposal"}
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}
